import React from 'react';
import { Redirect } from 'react-router-dom';
import { AxiosError } from 'axios';

import {
    createOidcAuthenticationComponent,
    createAuthorization,
    createDecoratedRoute,
    Spinner,
    AuthErrorType,
} from '@cp-shared-19/frontend-ui';

import { errorPagePath, landingPagePath, registrationPagePath } from '../components/navigation/paths';
import { getUserRegistryStatusEndpoint, UserRegistryStatus } from 'cpfr-common';
import { CpDataApi } from '../cp-xhr';

type AuthDataProviderResponse = { isAuthorized: boolean; error?: AxiosError };

const Authentication = createOidcAuthenticationComponent({
    onMissingAuthentication: <Redirect to={landingPagePath()} />,
});

const authDataProvider = async (): Promise<AuthDataProviderResponse> => {
    try {
        const { data: registrationData } = await CpDataApi.get<UserRegistryStatus>(getUserRegistryStatusEndpoint());
        return { isAuthorized: registrationData.isRegistered };
    } catch (e) {
        return { isAuthorized: false, error: e instanceof AxiosError ? e : undefined };
    }
};

export const getOidcErrorType = () => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('errorType') as AuthErrorType | null;
};

export const getErrorPagePathWithErrorType = (errorType: string) => errorPagePath() + `?errorType=${errorType}`;

const getErrorPageTypeFromAxiosError = (error: AuthDataProviderResponse['error']): AuthErrorType => {
    if (!(error instanceof AxiosError)) return AuthErrorType.UNEXPECTED_ERR;

    const responseCode = error?.response?.status;
    if (responseCode && responseCode >= 500 && responseCode < 600) {
        return AuthErrorType.GENERAL_ERR;
    }

    return AuthErrorType.UNEXPECTED_ERR;
};

const getRedirectPathOnMissingAuthorization = (res: AuthDataProviderResponse | never) => {
    const errorType = getOidcErrorType();
    const responseError = res?.error;
    const isAxiosError = responseError && responseError instanceof AxiosError;
    const isConnectionError =
        isAxiosError &&
        (responseError.code === 'ERR_NETWORK' ||
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            (responseError!.response && responseError!.response.status >= 500 && responseError!.response.status < 600));
    const errorPageType = errorType ? errorType : getErrorPageTypeFromAxiosError(responseError);
    return errorType || isConnectionError ? getErrorPagePathWithErrorType(errorPageType) : registrationPagePath();
};

const commonAuthorizationOptions = {
    onLoading: (
        <Spinner center>
            <p>
                Veuillez ne pas actualiser ni fermer cette page pendant le traitement de votre demande. Merci de votre
                patience.
            </p>
        </Spinner>
    ),
    onError: !getOidcErrorType() ? (
        <Redirect to={landingPagePath()} />
    ) : (
        <Redirect to={`${errorPagePath()}?errorType=${getOidcErrorType()}`} />
    ),
};

const { Authorization: UserRegistrationAuthorization, AuthorizationProvider: UserRegistrationProvider } =
    createAuthorization({
        displayName: 'UserRegistrationAuthorization',
        authorizationDataProvider: authDataProvider,
        onMissingAuthorization: (res) => <Redirect to={getRedirectPathOnMissingAuthorization(res)} />,
        ...commonAuthorizationOptions,
    });
export { UserRegistrationProvider };
export const AuthenticatedRoute = createDecoratedRoute('AuthenticatedRoute', Authentication);
export const AuthorizedRoute = createDecoratedRoute('AuthorizedRoute', Authentication, UserRegistrationAuthorization);
